import { useEffect, useRef, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useGetCurrentUserQuery,
  useSignPersonalFinancialStatementMutation,
  useUpdatePersonalFinancialStatementMutation,
  useUpdateRealEstateOwnedMutation,
} from '@/apis/extended/userApi';
import { CommonNumberInput } from '@/components/Common/CommonNumberInput';
import { TasteSectionCard } from '@/components/Common/TasteSectionCard';
import { formatPrice } from '@/utils/helper';
import { computePfsTotalsClient } from '@/utils/pfs';
import type {
  PersonalFinancialStatement,
  PfsIncome,
  PfsPerson,
  RealEstateOwnedRow,
} from '@/types/PfsType';
import { PfsScheduleTable } from './PfsScheduleTable';
import { ScheduleOfRealEstateOwnedForm } from './ScheduleOfRealEstateOwnedForm';

function emptyPerson(): PfsPerson {
  return {
    fullName: '',
    dob: '',
    ssn: '',
    homePhone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'United States',
  };
}

function emptyIncome(): PfsIncome {
  return {
    baseEmploymentIncome: 0,
    overtime: 0,
    commissions: 0,
    dividendsInterest: 0,
    rentalIncome: 0,
    alimonyChildSupport: 0,
    alimonyChildSupportDisclosed: false,
    otherIncome1: 0,
    otherIncome1Description: '',
    otherIncome2: 0,
    otherIncome2Description: '',
    otherIncome3: 0,
    otherIncome3Description: '',
  };
}

function PersonFields({
  title,
  value,
  onChange,
  readOnly,
}: {
  title: string;
  value: PfsPerson;
  onChange: (next: PfsPerson) => void;
  readOnly?: boolean;
}) {
  const set = (patch: Partial<PfsPerson>) => onChange({ ...value, ...patch });
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="Full name"
            disabled={readOnly}
            value={value.fullName ?? ''}
            onChange={(e) => set({ fullName: e.target.value })}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Date of birth"
            disabled={readOnly}
            value={value.dob ?? ''}
            onChange={(e) => set({ dob: e.target.value })}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label={
              value.ssnLast4 ? `SSN (on file: •••-••-${value.ssnLast4})` : 'SSN'
            }
            placeholder={value.ssnLast4 ? 'Leave blank to keep on file' : ''}
            disabled={readOnly}
            value={value.ssn ?? ''}
            onChange={(e) => set({ ssn: e.target.value })}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Phone"
            disabled={readOnly}
            value={value.homePhone ?? ''}
            onChange={(e) => set({ homePhone: e.target.value })}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Email"
            disabled={readOnly}
            value={value.email ?? ''}
            onChange={(e) => set({ email: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="Address"
            disabled={readOnly}
            value={value.address ?? ''}
            onChange={(e) => set({ address: e.target.value })}
          />
        </Grid>
        <Grid item xs={4} md={3}>
          <TextField
            fullWidth
            size="small"
            label="City"
            disabled={readOnly}
            value={value.city ?? ''}
            onChange={(e) => set({ city: e.target.value })}
          />
        </Grid>
        <Grid item xs={4} md={2}>
          <TextField
            fullWidth
            size="small"
            label="State"
            disabled={readOnly}
            value={value.state ?? ''}
            onChange={(e) => set({ state: e.target.value })}
          />
        </Grid>
        <Grid item xs={4} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Zip"
            disabled={readOnly}
            value={value.zip ?? ''}
            onChange={(e) => set({ zip: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Country"
            disabled={readOnly}
            value={value.country ?? ''}
            onChange={(e) => set({ country: e.target.value })}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

function IncomeFields({
  title,
  value,
  onChange,
  readOnly,
}: {
  title: string;
  value: PfsIncome;
  onChange: (next: PfsIncome) => void;
  readOnly?: boolean;
}) {
  const set = (patch: Partial<PfsIncome>) => onChange({ ...value, ...patch });
  const numberField = (key: keyof PfsIncome, label: string, md = 3) => (
    <Grid item xs={6} md={md} key={key}>
      <CommonNumberInput
        fullWidth
        size="small"
        label={label}
        disabled={readOnly}
        value={(value[key] as number) ?? ''}
        onChange={(e) =>
          set({ [key]: Number(e.target.value) || 0 } as Partial<PfsIncome>)
        }
        InputProps={{ startAdornment: '$' }}
      />
    </Grid>
  );
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Grid container spacing={1.5}>
        {numberField('baseEmploymentIncome', 'Base employment')}
        {numberField('overtime', 'Overtime')}
        {numberField('commissions', 'Commissions')}
        {numberField('dividendsInterest', 'Dividends / interest')}
        {numberField('rentalIncome', 'Rental income')}
        <Grid item xs={12} md={9}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(value.alimonyChildSupportDisclosed)}
                disabled={readOnly}
                onChange={(e) =>
                  set({ alimonyChildSupportDisclosed: e.target.checked })
                }
              />
            }
            label="I wish to have alimony/child support income considered for repayment"
          />
        </Grid>
        {value.alimonyChildSupportDisclosed &&
          numberField('alimonyChildSupport', 'Alimony / child support', 3)}
        {numberField('otherIncome1', 'Other income 1')}
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Describe"
            disabled={readOnly}
            value={value.otherIncome1Description ?? ''}
            onChange={(e) => set({ otherIncome1Description: e.target.value })}
          />
        </Grid>
        {numberField('otherIncome2', 'Other income 2')}
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Describe"
            disabled={readOnly}
            value={value.otherIncome2Description ?? ''}
            onChange={(e) => set({ otherIncome2Description: e.target.value })}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export function PersonalFinancialStatementForm() {
  const navigate = useNavigate();
  const { data: user } = useGetCurrentUserQuery();
  const [updatePfs, { isLoading: savingPfs }] =
    useUpdatePersonalFinancialStatementMutation();
  const [updateRealEstate, { isLoading: savingRealEstate }] =
    useUpdateRealEstateOwnedMutation();
  const [signPfs, { isLoading: signing }] =
    useSignPersonalFinancialStatementMutation();

  const [pfs, setPfs] = useState<PersonalFinancialStatement>({});
  const [realEstateOwned, setRealEstateOwned] = useState<RealEstateOwnedRow[]>(
    []
  );
  const [includeSpouse, setIncludeSpouse] = useState(false);

  const seededRef = useRef(false);
  useEffect(() => {
    if (!seededRef.current && user) {
      setPfs(user.personalFinancialStatement ?? {});
      setRealEstateOwned(user.realEstateOwned ?? []);
      setIncludeSpouse(
        Boolean(user.personalFinancialStatement?.spouse?.fullName)
      );
      seededRef.current = true;
    }
  }, [user]);

  const applicant = pfs.applicant ?? emptyPerson();
  const spouse = pfs.spouse ?? emptyPerson();
  const applicantIncome = pfs.applicantIncome ?? emptyIncome();
  const spouseIncome = pfs.spouseIncome ?? emptyIncome();

  const totals = computePfsTotalsClient(pfs, realEstateOwned);
  const isSigned = Boolean(user?.pfsSignedAt);

  const saveDraft = async () => {
    try {
      await Promise.all([
        updatePfs(pfs).unwrap(),
        updateRealEstate(realEstateOwned).unwrap(),
      ]);
      toast.success('Saved');
    } catch {
      toast.error('Could not save. Try again.');
    }
  };

  const signAndSubmit = async () => {
    try {
      await Promise.all([
        updatePfs(pfs).unwrap(),
        updateRealEstate(realEstateOwned).unwrap(),
      ]);
      await signPfs().unwrap();
      toast.success(
        'Personal Financial Statement signed — now add the property you are financing'
      );
      // The PFS qualifies them; the next step is the property it's for.
      navigate('/properties?add=1');
    } catch (e: unknown) {
      const message =
        (e as { data?: string })?.data ||
        'Complete the applicant name and at least one schedule before signing.';
      toast.error(message);
    }
  };

  const isSaving = savingPfs || savingRealEstate;

  return (
    <Box>
      {isSigned ? (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate('/properties?add=1')}
            >
              Add a property
            </Button>
          }
        >
          Your Personal Financial Statement is signed. Next, add the property
          you&apos;re financing to see commercial programs and rates for it.
        </Alert>
      ) : null}
      <TasteSectionCard sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Applicant
        </Typography>
        <PersonFields
          title="Applicant"
          value={applicant}
          onChange={(next) => setPfs({ ...pfs, applicant: next })}
          readOnly={isSigned}
        />

        <FormControlLabel
          sx={{ mb: 1 }}
          control={
            <Checkbox
              checked={includeSpouse}
              disabled={isSigned}
              onChange={(e) => setIncludeSpouse(e.target.checked)}
            />
          }
          label="Add a spouse / co-applicant"
        />
        {includeSpouse && (
          <PersonFields
            title="Spouse"
            value={spouse}
            onChange={(next) => setPfs({ ...pfs, spouse: next })}
            readOnly={isSigned}
          />
        )}
      </TasteSectionCard>

      <TasteSectionCard sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Annual income
        </Typography>
        <IncomeFields
          title="Applicant income"
          value={applicantIncome}
          onChange={(next) => setPfs({ ...pfs, applicantIncome: next })}
          readOnly={isSigned}
        />
        {includeSpouse && (
          <IncomeFields
            title="Spouse income"
            value={spouseIncome}
            onChange={(next) => setPfs({ ...pfs, spouseIncome: next })}
            readOnly={isSigned}
          />
        )}
        <Typography variant="body2" fontWeight={600}>
          Total annual income: {formatPrice(totals.totalAnnualIncome, 0)}
        </Typography>
      </TasteSectionCard>

      <TasteSectionCard sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Assets
        </Typography>
        <PfsScheduleTable
          title="Schedule A — Cash, checking & savings, CDs"
          columns={[
            { key: 'bankName', label: 'Bank', type: 'text', gridMd: 4 },
            {
              key: 'accountDescription',
              label: 'Account description',
              type: 'text',
              gridMd: 5,
            },
            {
              key: 'currentBalance',
              label: 'Balance',
              type: 'currency',
              gridMd: 3,
            },
          ]}
          rows={pfs.cashAccounts ?? []}
          onChange={(rows) => setPfs({ ...pfs, cashAccounts: rows })}
          emptyRow={() => ({
            bankName: '',
            accountDescription: '',
            currentBalance: 0,
          })}
          totalKey="currentBalance"
          addLabel="Add account"
          readOnly={isSigned}
        />
        <PfsScheduleTable
          title="Schedule B — Publicly traded investments"
          hint="Brokerage firms, stocks, bonds, mutual funds, IRAs, 401k."
          columns={[
            {
              key: 'namesRegistered',
              label: 'Name(s) registered',
              type: 'text',
              gridMd: 4,
            },
            {
              key: 'accountDescription',
              label: 'Account',
              type: 'text',
              gridMd: 4,
            },
            {
              key: 'currentValue',
              label: 'Current value',
              type: 'currency',
              gridMd: 4,
            },
          ]}
          rows={pfs.investments ?? []}
          onChange={(rows) => setPfs({ ...pfs, investments: rows })}
          emptyRow={() => ({
            namesRegistered: '',
            accountDescription: '',
            sharesAmt: '',
            retirement: false,
            currentValue: 0,
          })}
          totalKey="currentValue"
          addLabel="Add investment"
          readOnly={isSigned}
        />
        <PfsScheduleTable
          title="Schedule C — Other assets"
          hint="Vehicles, boats, personal assets, cash-value life insurance."
          columns={[
            {
              key: 'propertyDescription',
              label: 'Description',
              type: 'text',
              gridMd: 5,
            },
            {
              key: 'namesRegisteredIn',
              label: 'Registered in',
              type: 'text',
              gridMd: 4,
            },
            {
              key: 'currentValue',
              label: 'Current value',
              type: 'currency',
              gridMd: 3,
            },
          ]}
          rows={pfs.otherAssets ?? []}
          onChange={(rows) => setPfs({ ...pfs, otherAssets: rows })}
          emptyRow={() => ({
            propertyDescription: '',
            namesRegisteredIn: '',
            currentValue: 0,
          })}
          totalKey="currentValue"
          addLabel="Add asset"
          readOnly={isSigned}
        />
        <CommonNumberInput
          fullWidth
          size="small"
          sx={{ maxWidth: 320 }}
          label="Net worth of business(es) owned"
          disabled={isSigned}
          value={pfs.businessNetWorth ?? ''}
          onChange={(e) =>
            setPfs({ ...pfs, businessNetWorth: Number(e.target.value) || 0 })
          }
          InputProps={{ startAdornment: '$' }}
        />
        <Divider sx={{ my: 2 }} />
        <Typography variant="body1" fontWeight={700}>
          Total assets: {formatPrice(totals.totalAssets, 0)}
        </Typography>
      </TasteSectionCard>

      <TasteSectionCard sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Liabilities
        </Typography>
        <PfsScheduleTable
          title="Schedule D — Revolving credit"
          hint="Credit cards, credit lines (not home equity lines)."
          columns={[
            {
              key: 'toWhomPayable',
              label: 'To whom payable',
              type: 'text',
              gridMd: 4,
            },
            {
              key: 'commitmentAmt',
              label: 'Commitment',
              type: 'currency',
              gridMd: 3,
            },
            {
              key: 'monthlyPmt',
              label: 'Monthly pmt',
              type: 'currency',
              gridMd: 2,
            },
            {
              key: 'currentBalance',
              label: 'Balance',
              type: 'currency',
              gridMd: 3,
            },
          ]}
          rows={pfs.revolvingCredit ?? []}
          onChange={(rows) => setPfs({ ...pfs, revolvingCredit: rows })}
          emptyRow={() => ({
            toWhomPayable: '',
            description: '',
            commitmentAmt: 0,
            monthlyPmt: 0,
            currentBalance: 0,
          })}
          totalKey="currentBalance"
          addLabel="Add revolving account"
          readOnly={isSigned}
        />
        <PfsScheduleTable
          title="Schedule E — Installment loans"
          columns={[
            {
              key: 'toWhomPayable',
              label: 'To whom payable',
              type: 'text',
              gridMd: 4,
            },
            {
              key: 'originalAmt',
              label: 'Original amount',
              type: 'currency',
              gridMd: 3,
            },
            {
              key: 'monthlyPmt',
              label: 'Monthly pmt',
              type: 'currency',
              gridMd: 2,
            },
            {
              key: 'currentBalance',
              label: 'Balance',
              type: 'currency',
              gridMd: 3,
            },
          ]}
          rows={pfs.installmentLoans ?? []}
          onChange={(rows) => setPfs({ ...pfs, installmentLoans: rows })}
          emptyRow={() => ({
            toWhomPayable: '',
            description: '',
            originalAmt: 0,
            monthlyPmt: 0,
            currentBalance: 0,
          })}
          totalKey="currentBalance"
          addLabel="Add loan"
          readOnly={isSigned}
        />
        <PfsScheduleTable
          title="Schedule F — Other liabilities"
          columns={[
            {
              key: 'description',
              label: 'Description',
              type: 'text',
              gridMd: 5,
            },
            {
              key: 'monthlyPmt',
              label: 'Monthly pmt',
              type: 'currency',
              gridMd: 3,
            },
            {
              key: 'currentBalance',
              label: 'Balance',
              type: 'currency',
              gridMd: 4,
            },
          ]}
          rows={pfs.otherLiabilities ?? []}
          onChange={(rows) => setPfs({ ...pfs, otherLiabilities: rows })}
          emptyRow={() => ({
            description: '',
            monthlyPmt: 0,
            currentBalance: 0,
          })}
          totalKey="currentBalance"
          addLabel="Add liability"
          readOnly={isSigned}
        />
        <CommonNumberInput
          fullWidth
          size="small"
          sx={{ maxWidth: 320 }}
          label="Other liability"
          disabled={isSigned}
          value={pfs.otherLiabilityNote ?? ''}
          onChange={(e) =>
            setPfs({ ...pfs, otherLiabilityNote: Number(e.target.value) || 0 })
          }
          InputProps={{ startAdornment: '$' }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Mortgage loans are totaled automatically from the Schedule of Real
          Estate Owned below.
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body1" fontWeight={700}>
          Total liabilities: {formatPrice(totals.totalLiabilities, 0)}
        </Typography>
      </TasteSectionCard>

      <TasteSectionCard sx={{ p: 3, mb: 3 }}>
        <ScheduleOfRealEstateOwnedForm
          rows={realEstateOwned}
          onChange={setRealEstateOwned}
          readOnly={isSigned}
        />
      </TasteSectionCard>

      <TasteSectionCard sx={{ p: 3, mb: 3, position: 'sticky', bottom: 16 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h6" fontWeight={700}>
              Net worth: {formatPrice(totals.netWorth, 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total assets {formatPrice(totals.totalAssets, 0)} − total
              liabilities {formatPrice(totals.totalLiabilities, 0)}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}
          >
            {!isSigned && (
              <LoadingButton
                variant="outlined"
                loading={isSaving}
                onClick={() => void saveDraft()}
              >
                Save draft
              </LoadingButton>
            )}
            <LoadingButton
              variant="contained"
              loading={isSaving || signing}
              disabled={isSigned}
              onClick={() => void signAndSubmit()}
            >
              {isSigned ? 'Signed' : 'Sign & submit'}
            </LoadingButton>
          </Grid>
        </Grid>
      </TasteSectionCard>
    </Box>
  );
}
